import { createModel } from '@rematch/core';
import { selectors } from './selector';
import {
    Dispatch,
    RootState
} from '../../store';
import { Snomed } from '../../models/snomed';
import {
    getSnomeds,
    searchSnomeds
} from '../../apis/emr-data';

export interface State {
    allIds: string[];
    byId: Dictionary<Snomed>;
    loading: Dictionary<boolean>;
    searchedTextHash: Dictionary<boolean>;
}

export const snomeds = createModel<State>({
    state: {
        allIds: [],
        byId: {},
        loading: {},
        searchedTextHash: {}
    },
    reducers: {
        update: (
            state: State,
            data: Undefinable<MixType<Snomed>>,
            loadingKey?: string
        ): State => {
            const byId = {
                ...state.byId,
                ...storeByConceptId(state.byId, data)
            };
            return {
                ...state,
                allIds: Object.keys(byId)
                    .sort((a, b) => (byId[a]!.term || '') > (byId[b]!.term || '') ? 1 : -1)
                    .map(x => x),
                byId: byId,
                ...loadingKey && {
                    loading: {
                        ...state.loading,
                        [loadingKey]: false
                    }
                }
            };
        },
        loading: (
            state: State,
            loadingKey: string,
            loading: boolean
        ): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        }),
        setSearched: (state: State, text: string): State => ({
            ...state,
            searchedTextHash: {
                ...state.searchedTextHash,
                [text]: true
            }
        })
    },
    effects: dispatch => ({
        async getSnomedsAsync(
            payload: {
                conceptIds: Undefinable<number>[];
                active: boolean;
            },
            rootState: RootState
        ) {
            // @ts-ignore
            const { snomedCategories } = dispatch as Dispatch;
            const notExistedIds = selectors.getOtherSnomedIds(rootState.snomeds, payload.conceptIds);
            if (notExistedIds.length > 0) {
                try {
                    const data = await getSnomeds(notExistedIds, payload.active);
                    this.update(data);

                    // Collect related data
                    // @ts-ignore
                    snomedCategories.getSnomedCategoriesAsync();
                } catch (e) {
                    window.console.log(e);
                }
            }
        },
        async searchSnomedsAsync(text: string, rootState: RootState) {
            const loading = selectors.getLoading(rootState.snomeds, `searchSnomedsAsync---${text}`);
            if (!loading && !selectors.checkSearched(rootState.snomeds, text)) {
                this.loading(`searchSnomedsAsync---${text}`, true);
                const data = await searchSnomeds(text);
                this.update(data, `searchSnomedsAsync---${text}`);
                this.loading(`searchSnomedsAsync---${text}`, false);
                this.setSearched(text);
            }
        }
    })
});

const storeByConceptId = (initialById: Dictionary<Snomed>, data: Undefinable<MixType<Snomed>>): Dictionary<Snomed> => {
    const byId = {} as Dictionary<Snomed>;
    if (data) {
        if (Array.isArray(data)) {
            for (let i = 0; i < data.length; i++) {
                if (byId[data[i].conceptId]) {
                    byId[data[i].conceptId]!.relatedIds!.push(data[i].id);
                } else {
                    byId[data[i].conceptId] = {
                        ...initialById[data[i].conceptId],
                        ...data[i],
                        relatedIds: [data[i].id]
                    };
                }
            }
        } else {
            byId[data.conceptId] = {
                ...initialById[data.conceptId],
                ...data,
                relatedIds: [data.id]
            };
        }
    }
    return byId;
};
